import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import styles from "./createUserModal.module.css";
import Button from "../../../../components/button/button";
import {
  createMantenedoraUser,
  createInstitutionUser,
} from "../../../../services/mantenedora";

const CreateUserModal = ({
  type,
  institution,
  institutions = [],
  mantenedoraId,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedInstitutionIds, setSelectedInstitutionIds] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const isMantenedora = type === "mantenedora";

  const mantenedoraRoles = [
    { value: "MantenedoraAdmin", label: "Admin da Mantenedora" },
    { value: "MantenedoraManager", label: "Gerente da Mantenedora" },
  ];

  const institutionRoles = [
    { value: "Admin", label: "Administrador" },
    { value: "Manager", label: "Gerente" },
    { value: "Operator", label: "Operador" },
    { value: "Financial", label: "Financeiro" },
    { value: "Academic", label: "Acadêmico" },
  ];

  const roles = isMantenedora ? mantenedoraRoles : institutionRoles;

  const handleInstitutionToggle = (institutionId) => {
    setSelectedInstitutionIds((prev) =>
      prev.includes(institutionId)
        ? prev.filter((id) => id !== institutionId)
        : [...prev, institutionId]
    );
  };

  const handleSelectAllInstitutions = () => {
    if (selectedInstitutionIds.length === institutions.length) {
      setSelectedInstitutionIds([]);
    } else {
      setSelectedInstitutionIds(institutions.map((inst) => inst.institutionId));
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const userData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        phoneNumber: data.phoneNumber || null,
        role: data.role,
      };

      if (isMantenedora) {
        userData.institutionIds = selectedInstitutionIds;
        await createMantenedoraUser(mantenedoraId, userData);
      } else {
        await createInstitutionUser(mantenedoraId, institution.id, userData);
      }

      onSuccess();
    } catch (error) {
      toast.error(error.message || "Erro ao criar usuário");
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>
            {isMantenedora
              ? "Adicionar Usuário da Mantenedora"
              : `Adicionar Usuário - ${institution?.name}`}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>Nome *</label>
              <input
                type="text"
                {...register("firstName", {
                  required: "Nome é obrigatório",
                  minLength: {
                    value: 2,
                    message: "Nome deve ter pelo menos 2 caracteres",
                  },
                })}
                placeholder="Digite o nome"
              />
              {errors.firstName && (
                <span className={styles.error}>{errors.firstName.message}</span>
              )}
            </div>

            <div className={styles.field}>
              <label>Sobrenome *</label>
              <input
                type="text"
                {...register("lastName", {
                  required: "Sobrenome é obrigatório",
                  minLength: {
                    value: 2,
                    message: "Sobrenome deve ter pelo menos 2 caracteres",
                  },
                })}
                placeholder="Digite o sobrenome"
              />
              {errors.lastName && (
                <span className={styles.error}>{errors.lastName.message}</span>
              )}
            </div>
          </div>

          <div className={styles.field}>
            <label>E-mail *</label>
            <input
              type="email"
              {...register("email", {
                required: "E-mail é obrigatório",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "E-mail inválido",
                },
              })}
              placeholder="Digite o e-mail"
            />
            {errors.email && (
              <span className={styles.error}>{errors.email.message}</span>
            )}
          </div>

          <div className={styles.field}>
            <label>Telefone</label>
            <input
              type="tel"
              {...register("phoneNumber")}
              placeholder="(00) 00000-0000"
            />
          </div>

          <div className={styles.field}>
            <label>Perfil *</label>
            <select
              {...register("role", { required: "Selecione um perfil" })}
              defaultValue=""
            >
              <option value="" disabled>
                Selecione um perfil
              </option>
              {roles.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            {errors.role && (
              <span className={styles.error}>{errors.role.message}</span>
            )}
          </div>

          {isMantenedora && institutions.length > 0 && (
            <div className={`${styles.field} ${styles.institutionsSection}`}>
              <label>Acesso às Instituições</label>
              <p className={styles.institutionsHint}>
                Selecione as instituições que este usuário terá acesso.
              </p>
              <div className={styles.institutionsList}>
                <label className={styles.selectAllLabel}>
                  <input
                    type="checkbox"
                    checked={selectedInstitutionIds.length === institutions.length}
                    onChange={handleSelectAllInstitutions}
                  />
                  <span className={styles.selectAllText}>Selecionar todas</span>
                </label>
                {institutions.map((inst) => (
                  <label key={inst.institutionId} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedInstitutionIds.includes(inst.institutionId)}
                      onChange={() => handleInstitutionToggle(inst.institutionId)}
                    />
                    <span className={styles.checkboxText}>
                      {inst.institutionName}
                      {inst.city && inst.state && (
                        <span className={styles.checkboxSubtext}>
                          {" "} — {inst.city}, {inst.state}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className={styles.field}>
            <label>Senha *</label>
            <input
              type="password"
              {...register("password", {
                required: "Senha é obrigatória",
                minLength: {
                  value: 6,
                  message: "Senha deve ter pelo menos 6 caracteres",
                },
              })}
              placeholder="Digite a senha"
            />
            {errors.password && (
              <span className={styles.error}>{errors.password.message}</span>
            )}
          </div>

          <div className={styles.field}>
            <label>Confirmar Senha *</label>
            <input
              type="password"
              {...register("confirmPassword", {
                required: "Confirme a senha",
                validate: (value, formValues) =>
                  value === formValues.password || "As senhas não conferem",
              })}
              placeholder="Confirme a senha"
            />
            {errors.confirmPassword && (
              <span className={styles.error}>
                {errors.confirmPassword.message}
              </span>
            )}
          </div>

          <div className={styles.actions}>
            <Button
              type="button"
              text="Cancelar"
              variation="outline"
              size="normal"
              onClick={onClose}
            />
            <Button
              type="submit"
              text={loading ? "Criando..." : "Criar Usuário"}
              variation="primary"
              size="normal"
              disabled={loading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;
